export const CONTROLS_COVERAGE_SCHEMA_VERSIONS = [
  'controls-coverage.v1',
  'controls-coverage.v2',
] as const;

export type ControlsCoverageSchemaVersion =
  (typeof CONTROLS_COVERAGE_SCHEMA_VERSIONS)[number];

export type ControlsCoveragePageStatus =
  | 'complete'
  | 'partial'
  | 'notApplicable'
  | 'unsupported'
  | 'notAttempted';

/**
 * Fail-closed summary shared by the API and the report dashboard. `activity`
 * is added by org-level API aggregation when controls ran but no trustworthy
 * block is available.
 */
export type ControlsCoverageSummary =
  | {
      available: true;
      pagesAttempted: number;
      pagesFullyEvaluated: number;
      pagesPartial: number;
      pagesNotApplicable: number;
      pagesUnsupported: number;
      pagesNotAttempted: number;
      mayPresentAsClean: boolean;
    }
  | { available: false; activity?: boolean };

const KNOWN_COVERAGE_VERSIONS = new Set<string>(
  CONTROLS_COVERAGE_SCHEMA_VERSIONS,
);
const KNOWN_COVERAGE_STATUSES = new Set<ControlsCoveragePageStatus>([
  'complete',
  'partial',
  'notApplicable',
  'unsupported',
  'notAttempted',
]);
const V1_REASON_CODES = new Set([
  'artefactMissingOrLegacy',
  'axUnavailable',
  'axAmbiguous',
  'visualEvidenceMissing',
  'visualEvidenceUnstable',
  'frameUnreachable',
  'chunkFailed',
  'refusalOrContentFilter',
  'truncationRetryExhausted',
  'pageLimit',
  'costCap',
  'unsupportedSchemaVersion',
]);
const CONTROL_RULE_IDS = new Set([
  'EA_AI_R48',
  'EA_AI_R49',
  'EA_AI_R50',
  'EA_AI_R51',
  'EA_AI_R52',
  'EA_AI_R53',
  'EA_AI_R54',
  'EA_AI_R55',
]);
const CANDIDATE_DIAGNOSTIC_KEYS = new Set([
  'pageId',
  'candidateId',
  'selector',
  'stableKey',
  'reasons',
  'inconclusiveRuleIds',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nonNegativeInteger(
  record: Record<string, unknown>,
  key: string,
): number | null {
  const value = record[key];
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
    ? value
    : null;
}

function isUniqueAllowedStringArray(
  value: unknown,
  allowed: ReadonlySet<string>,
  minimumLength = 0,
): value is string[] {
  return (
    Array.isArray(value) &&
    value.length >= minimumLength &&
    value.every((item) => typeof item === 'string' && allowed.has(item)) &&
    new Set(value).size === value.length
  );
}

function isNullableBoundedString(value: unknown, maximumLength: number) {
  return (
    value === null ||
    (typeof value === 'string' && value.length <= maximumLength)
  );
}

function isValidCandidateDiagnostic(
  value: unknown,
  allowedReasons: ReadonlySet<string>,
): boolean {
  if (!isRecord(value)) return false;
  const keys = Object.keys(value);
  if (
    keys.length !== CANDIDATE_DIAGNOSTIC_KEYS.size ||
    keys.some((key) => !CANDIDATE_DIAGNOSTIC_KEYS.has(key))
  ) {
    return false;
  }

  return (
    typeof value.pageId === 'string' &&
    value.pageId.length >= 1 &&
    value.pageId.length <= 128 &&
    typeof value.candidateId === 'string' &&
    value.candidateId.length <= 32 &&
    /^[FX][1-9][0-9]*$/.test(value.candidateId) &&
    isNullableBoundedString(value.selector, 512) &&
    isNullableBoundedString(value.stableKey, 256) &&
    isUniqueAllowedStringArray(value.reasons, allowedReasons, 1) &&
    isUniqueAllowedStringArray(value.inconclusiveRuleIds, CONTROL_RULE_IDS)
  );
}

/**
 * Parse one producer coverage block (`report.coverage` or
 * `details.coverage`). The aggregates are never trusted on their own: they
 * must exactly match the per-page status map. Unknown versions/statuses,
 * malformed page entries, fractional/negative counts and inconsistent totals
 * all return `available: false`.
 */
export function parseControlsCoverageBlock(
  coverage: unknown,
): ControlsCoverageSummary {
  if (!isRecord(coverage)) return { available: false };
  if (
    typeof coverage.schemaVersion !== 'string' ||
    !KNOWN_COVERAGE_VERSIONS.has(coverage.schemaVersion)
  ) {
    return { available: false };
  }
  if (!isRecord(coverage.controls)) return { available: false };

  const controls = coverage.controls;
  const schemaVersion = coverage.schemaVersion as ControlsCoverageSchemaVersion;
  const allowedReasons =
    schemaVersion === 'controls-coverage.v2'
      ? new Set([...V1_REASON_CODES, 'modelInconclusive'])
      : V1_REASON_CODES;
  if (
    schemaVersion === 'controls-coverage.v2' &&
    (!Array.isArray(controls.candidateDiagnostics) ||
      controls.candidateDiagnostics.some(
        (diagnostic) => !isValidCandidateDiagnostic(diagnostic, allowedReasons),
      ))
  ) {
    return { available: false };
  }
  if (!isRecord(controls.pages)) return { available: false };

  const recomputed: Record<ControlsCoveragePageStatus, number> = {
    complete: 0,
    partial: 0,
    notApplicable: 0,
    unsupported: 0,
    notAttempted: 0,
  };

  for (const page of Object.values(controls.pages)) {
    if (!isRecord(page) || typeof page.status !== 'string') {
      return { available: false };
    }
    const status = page.status as ControlsCoveragePageStatus;
    if (!KNOWN_COVERAGE_STATUSES.has(status)) {
      return { available: false };
    }
    if (!Array.isArray(page.reasons)) return { available: false };
    const reasons = page.reasons;
    const requiresReason =
      status === 'partial' ||
      (schemaVersion === 'controls-coverage.v2' &&
        (status === 'unsupported' || status === 'notAttempted'));
    if (
      reasons.some(
        (reason) => typeof reason !== 'string' || !allowedReasons.has(reason),
      ) ||
      new Set(reasons).size !== reasons.length ||
      (requiresReason && reasons.length === 0) ||
      ((status === 'complete' || status === 'notApplicable') &&
        reasons.length > 0)
    ) {
      return { available: false };
    }
    recomputed[status] += 1;
  }

  const pagesAttempted = nonNegativeInteger(controls, 'pagesAttempted');
  const pagesFullyEvaluated = nonNegativeInteger(
    controls,
    'pagesFullyEvaluated',
  );
  const pagesPartial = nonNegativeInteger(controls, 'pagesPartial');
  const pagesNotApplicable = nonNegativeInteger(controls, 'pagesNotApplicable');
  const pagesUnsupported = nonNegativeInteger(controls, 'pagesUnsupported');
  const pagesNotAttempted = nonNegativeInteger(controls, 'pagesNotAttempted');

  if (
    pagesAttempted === null ||
    pagesFullyEvaluated === null ||
    pagesPartial === null ||
    pagesNotApplicable === null ||
    pagesUnsupported === null ||
    pagesNotAttempted === null
  ) {
    return { available: false };
  }

  const pageCount = Object.keys(controls.pages).length;
  const consistent =
    pagesFullyEvaluated === recomputed.complete &&
    pagesPartial === recomputed.partial &&
    pagesNotApplicable === recomputed.notApplicable &&
    pagesUnsupported === recomputed.unsupported &&
    pagesNotAttempted === recomputed.notAttempted &&
    pagesAttempted ===
      recomputed.complete +
        recomputed.partial +
        recomputed.notApplicable +
        recomputed.unsupported &&
    pagesAttempted + recomputed.notAttempted === pageCount;

  if (!consistent) return { available: false };

  return {
    available: true,
    pagesAttempted,
    pagesFullyEvaluated,
    pagesPartial,
    pagesNotApplicable,
    pagesUnsupported,
    pagesNotAttempted,
    mayPresentAsClean:
      pagesFullyEvaluated >= 1 &&
      pagesPartial === 0 &&
      pagesUnsupported === 0 &&
      pagesNotAttempted === 0,
  };
}

/** Parse `aiTestRuns.details` without duplicating contract logic in the API. */
export function parseControlsCoverageDetails(
  details: unknown,
): ControlsCoverageSummary {
  return isRecord(details)
    ? parseControlsCoverageBlock(details.coverage)
    : { available: false };
}
